import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Adjustment, AdjustmentFormData, Apartment } from './types';

interface AdjustmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingAdjustment: Adjustment | null;
  form: AdjustmentFormData;
  onFormChange: (form: AdjustmentFormData) => void;
  onSave: () => void;
  apartments: Apartment[];
}

export function AdjustmentDialog({
  open,
  onOpenChange,
  editingAdjustment,
  form,
  onFormChange,
  onSave,
  apartments
}: AdjustmentDialogProps) {
  const handleApartmentToggle = (aptId: string, checked: boolean) => {
    if (checked) {
      onFormChange({ ...form, apartment_ids: [...form.apartment_ids, aptId] });
    } else {
      onFormChange({ ...form, apartment_ids: form.apartment_ids.filter(id => id !== aptId) });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{editingAdjustment ? 'Redigera justering' : 'Ny justering'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Namn *</Label>
            <Input
              value={form.name}
              onChange={(e) => onFormChange({ ...form, name: e.target.value })}
              placeholder="T.ex. Seniorrabatt"
              data-testid="adjustment-name-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Typ</Label>
              <Select
                value={form.type}
                onValueChange={(value: 'discount' | 'surcharge') => 
                  onFormChange({ ...form, type: value })
                }
              >
                <SelectTrigger data-testid="adjustment-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="discount">Rabatt</SelectItem>
                  <SelectItem value="surcharge">Pålägg</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Beloppstyp</Label>
              <Select
                value={form.amount_type}
                onValueChange={(value: 'fixed' | 'percentage') => 
                  onFormChange({ ...form, amount_type: value })
                }
              >
                <SelectTrigger data-testid="adjustment-amount-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fixed">Fast belopp (kr)</SelectItem>
                  <SelectItem value="percentage">Procent (%)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Belopp *</Label>
              <Input
                type="number"
                value={form.amount}
                onChange={(e) => onFormChange({ ...form, amount: e.target.value })}
                placeholder="0"
                data-testid="adjustment-amount-input"
              />
            </div>
            <div>
              <Label>Frekvens</Label>
              <Select
                value={form.frequency}
                onValueChange={(value: 'recurring' | 'once') => 
                  onFormChange({ ...form, frequency: value })
                }
              >
                <SelectTrigger data-testid="adjustment-frequency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recurring">Återkommande</SelectItem>
                  <SelectItem value="once">Engångs</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Gäller för</Label>
            <Select
              value={form.applies_to}
              onValueChange={(value: 'all' | 'specific') => 
                onFormChange({ ...form, applies_to: value })
              }
            >
              <SelectTrigger data-testid="adjustment-applies-to-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alla lägenheter</SelectItem>
                <SelectItem value="specific">Specifika lägenheter</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.applies_to === 'specific' && (
            <div>
              <Label>Välj lägenheter</Label>
              <div className="max-h-40 overflow-y-auto border rounded-lg p-2 space-y-1">
                {apartments.map(apt => (
                  <label key={apt.id} className="flex items-center gap-2 p-1 hover:bg-slate-50 rounded">
                    <input
                      type="checkbox"
                      checked={form.apartment_ids.includes(apt.id)}
                      onChange={(e) => handleApartmentToggle(apt.id, e.target.checked)}
                      data-testid={`adjustment-apt-${apt.apartment_number}`}
                    />
                    <span className="text-sm">{apt.apartment_number}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="adj-active"
              checked={form.is_active}
              onChange={(e) => onFormChange({ ...form, is_active: e.target.checked })}
              data-testid="adjustment-active-checkbox"
            />
            <label htmlFor="adj-active" className="text-sm">Aktiv</label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Avbryt</Button>
          <Button onClick={onSave} data-testid="adjustment-save-btn">Spara</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
